import { warehouseClient } from "@/infrastructure/http/warehouseClient";
import type { Transfer, TransferListResponse, CreateTransferInput, AddTransferLineInput, ApiEnvelope } from "@/domain/types";
import axios from "axios";

function getLocale(): string {
  try { return localStorage.getItem("nexo-locale") ?? "es"; } catch { return "es"; }
}

const ERROR_TRANSLATIONS: Record<string, string> = {
  "Source and destination warehouses cannot be the same": "Source and destination warehouses cannot be the same",
  "Only draft transfers can be edited or confirmed": "Only draft transfers can be edited or confirmed",
  "Only confirmed transfers can be cancelled": "Only confirmed transfers can be cancelled",
  "Transfer must have at least one detail line to be confirmed": "Transfer must have at least one line to be confirmed",
  "Cancellation reason is required": "Cancellation reason is required",
  "Stock insuficiente": "Insufficient stock",
  "Capacidad de ubicación excedida": "Location capacity exceeded",
  "Transfer not found": "Transfer not found",
};

function translateError(msg: string): string {
  if (getLocale() === "es") return msg;
  for (const [es, en] of Object.entries(ERROR_TRANSLATIONS)) {
    if (msg.includes(es)) return msg.replace(es, en);
  }
  return msg;
}

function extractMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return "Error";
  const data = err.response?.data;
  if (data && typeof data === "object" && "message" in data) {
    const msg = (data as Record<string, unknown>).message;
    if (typeof msg === "string") return translateError(msg);
    if (Array.isArray(msg) && msg.length > 0) return translateError(String(msg[0]));
  }
  return "Error";
}

export function isTransferError(err: unknown): err is Error & { name: "TransferError" } {
  return err instanceof Error && err.name === "TransferError";
}

function throwTransferError(err: unknown): never {
  const msg = extractMessage(err);
  const e = new Error(msg);
  e.name = "TransferError";
  throw e;
}

export async function listTransfers(params: { page?: number; limit?: number; status?: string; warehouseFromId?: string; warehouseToId?: string; from?: string; to?: string }): Promise<TransferListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.status) query.set("status", params.status);
  if (params.warehouseFromId) query.set("warehouseFromId", params.warehouseFromId);
  if (params.warehouseToId) query.set("warehouseToId", params.warehouseToId);
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  const response = await warehouseClient.get<ApiEnvelope<TransferListResponse>>(`/transfer?${query.toString()}`);
  return response.data.data;
}

export async function getTransfer(id: string): Promise<Transfer> {
  const response = await warehouseClient.get<ApiEnvelope<Transfer>>(`/transfer/${id}`);
  return response.data.data;
}

export async function createTransfer(input: CreateTransferInput): Promise<Transfer> {
  try {
    const response = await warehouseClient.post<ApiEnvelope<Transfer>>("/transfer", input);
    return response.data.data;
  } catch (err) { throwTransferError(err); }
}

export async function updateTransfer(id: string, input: Partial<Pick<CreateTransferInput, "reason" | "notes">>): Promise<void> {
  try {
    await warehouseClient.patch(`/transfer/${id}`, input);
  } catch (err) { throwTransferError(err); }
}

export class TransferLotsError extends Error {
  availableLots: string[];
  constructor(message: string, lots: string[]) {
    super(message);
    this.name = "TransferLotsError";
    this.availableLots = lots;
  }
}

export async function addTransferLine(transferId: string, input: AddTransferLineInput): Promise<void> {
  try {
    await warehouseClient.post(`/transfer/${transferId}/lines`, input);
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const data = err.response?.data;
      if (data && typeof data === "object" && "data" in data) {
        const responseData = (data as any).data;
        if (responseData?.availableLots && Array.isArray(responseData.availableLots)) {
          const msg = extractMessage(err);
          throw new TransferLotsError(msg, responseData.availableLots);
        }
      }
    }
    throwTransferError(err);
  }
}

export async function updateTransferLine(transferId: string, lineId: string, input: Partial<AddTransferLineInput>): Promise<void> {
  try {
    await warehouseClient.patch(`/transfer/${transferId}/lines/${lineId}`, input);
  } catch (err) { throwTransferError(err); }
}

export async function deleteTransferLine(transferId: string, lineId: string): Promise<void> {
  try {
    await warehouseClient.delete(`/transfer/${transferId}/lines/${lineId}`);
  } catch (err) { throwTransferError(err); }
}

export async function confirmTransfer(id: string): Promise<void> {
  try {
    await warehouseClient.post(`/transfer/${id}/confirm`);
  } catch (err) { throwTransferError(err); }
}

export async function cancelTransfer(id: string, reason: string): Promise<void> {
  try {
    await warehouseClient.post(`/transfer/${id}/cancel`, { reason });
  } catch (err) { throwTransferError(err); }
}
